import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, Loader2, Sparkles, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { queryKeys } from '@/queries/keys'
import { tagSuggestionsService, type TagSuggestion } from '@/services/tagSuggestionsService'

/**
 * Lo que un modelo propone para esta conversación, a la espera de una persona.
 * Sin propuestas no ocupa sitio: la mayoría de las conversaciones no tendrá.
 */
export function TagSuggestions({ conversationId, onConfirmed }: { conversationId: string; onConfirmed?: () => void }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const suggestions = useQuery({
    queryKey: queryKeys.tagSuggestions.byConversation(conversationId),
    queryFn: () => tagSuggestionsService.list(conversationId),
    enabled: Boolean(conversationId),
  })
  const decide = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'confirm' | 'discard' }) => tagSuggestionsService.decide(id, action),
    onSuccess: (_data, { action }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.tagSuggestions.byConversation(conversationId) })
      if (action === 'confirm') {
        toast.success(t('tag_suggestions.confirmed'))
        onConfirmed?.()
      } else {
        toast.success(t('tag_suggestions.discarded'))
      }
    },
    onError: (error: Error) => toast.error(error.message),
  })

  if (!suggestions.data?.length) return null

  return (
    <div className="space-y-2" data-testid="tag-suggestions">
      <p className="flex items-center gap-1 text-xs font-medium text-muted-foreground">
        <Sparkles className="h-3 w-3" />
        {t('tag_suggestions.title')}
      </p>
      <ul className="space-y-1">
        {suggestions.data.map((suggestion) => (
          <SuggestionRow
            key={suggestion.id}
            suggestion={suggestion}
            busy={decide.isPending && decide.variables?.id === suggestion.id}
            onDecide={(action) => decide.mutate({ id: suggestion.id, action })}
          />
        ))}
      </ul>
    </div>
  )
}

function SuggestionRow({
  suggestion,
  busy,
  onDecide,
}: {
  suggestion: TagSuggestion
  busy: boolean
  onDecide: (action: 'confirm' | 'discard') => void
}) {
  const { t } = useTranslation()
  const percent = suggestion.probability === null ? null : Math.round(suggestion.probability * 100)
  return (
    <li className="flex items-center justify-between gap-2 rounded-md bg-muted/50 px-2 py-1 text-sm">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="min-w-0 truncate">
              {suggestion.tag.name}
              {percent !== null ? <span className="ml-1 text-xs text-muted-foreground">{percent}%</span> : null}
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            {suggestion.message_text ? t('tag_suggestions.because', { message: suggestion.message_text }) : t('tag_suggestions.no_message')}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <span className="flex shrink-0 gap-1">
        <Button
          variant="ghost"
          size="icon-xs"
          disabled={busy}
          aria-label={t('tag_suggestions.confirm', { tag: suggestion.tag.name })}
          title={t('tag_suggestions.confirm', { tag: suggestion.tag.name })}
          onClick={() => onDecide('confirm')}
        >
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
        </Button>
        <Button
          variant="ghost"
          size="icon-xs"
          disabled={busy}
          aria-label={t('tag_suggestions.discard', { tag: suggestion.tag.name })}
          title={t('tag_suggestions.discard', { tag: suggestion.tag.name })}
          onClick={() => onDecide('discard')}
        >
          <X className="h-3 w-3" />
        </Button>
      </span>
    </li>
  )
}
